/**
 * Decision-quality signals derived from a listing.
 *
 * The goal is to translate raw deal numbers into a tight list of pros / cons
 * the user can scan in <2 seconds. We compute:
 *
 *   - **pros**: positive signals (super deal, recent price drop, fresh listing,
 *     dealer warranty, well-photographed, low km for year, …)
 *   - **cons**: negative signals (above market, no photos, very old listing,
 *     extreme km, suspicious savings, low cohort confidence, …)
 *   - **risks**: explicit warnings the buyer should investigate (km/year
 *     mismatch, low confidence on rating, suspicious savings)
 *
 * This util has **zero runtime dependency** on tRPC types — it accepts the
 * minimum subset of fields. That means the same logic can be used in cards,
 * the comparison panel, the scatter tooltip, and the listing detail header.
 */

import type { DealRating } from "./utils";

/**
 * Minimal listing shape for signal computation. Only fields actually used.
 * Adding a field here forces every call-site to opt-in.
 */
export interface SignalInput {
  dealRating: DealRating | string | null | undefined;
  dealSavingsEur: number | null | undefined;
  dealSavingsRawEur?: number | null | undefined;
  fairPriceEur: number | null | undefined;
  fairPriceConfidence?: string | null | undefined;
  savingsSuspect?: boolean | null | undefined;
  currentPriceEur: number | null | undefined;
  currentKm: number | null | undefined;
  currentYear: number | null | undefined;
  firstSeenAt?: Date | string | null | undefined;
  recentPriceDropEur?: number | null | undefined;
  photoCount?: number | null | undefined;
  sellerType?: string | null | undefined;
  dupCount?: number | null | undefined;
}

export interface Signal {
  /** Stable id (i18n key suffix). Useful for de-dup and tracking. */
  id: string;
  /** Polarity. */
  kind: "pro" | "con" | "risk";
  /** Short human label (already localised). */
  label: string;
  /** Optional secondary label (e.g. amount). */
  detail?: string;
  /** Tiny tone hint for UI, mirrors `kind` but lets future variations exist. */
  tone: "positive" | "negative" | "warning" | "neutral";
}

/**
 * Compute the days-on-market from `firstSeenAt`, capped at a sane maximum.
 * Returns `null` if we can't tell.
 */
export function daysOnMarket(input: Pick<SignalInput, "firstSeenAt">): number | null {
  if (!input.firstSeenAt) return null;
  const t =
    typeof input.firstSeenAt === "string"
      ? new Date(input.firstSeenAt).getTime()
      : input.firstSeenAt instanceof Date
        ? input.firstSeenAt.getTime()
        : null;
  if (t == null || Number.isNaN(t)) return null;
  return Math.max(0, Math.floor((Date.now() - t) / 86_400_000));
}

/**
 * Heuristic: average annual mileage for a passenger car in the EU is ~14 000
 * km/year. Listings under 8 000 km/year are "low km", over 25 000 km/year are
 * "very high km". These thresholds are forgiving on purpose — the goal is to
 * surface OUTLIERS, not nitpick averages.
 */
function kmPerYear(input: Pick<SignalInput, "currentKm" | "currentYear">): number | null {
  if (input.currentKm == null || input.currentYear == null) return null;
  const age = Math.max(1, new Date().getFullYear() - input.currentYear);
  return Math.round(input.currentKm / age);
}

interface Localiser {
  /** Translation function. Falls back to the key if missing. */
  t: (key: string, vars?: Record<string, string | number>) => string;
  /** Locale for amount formatting. */
  numberLocale: string;
}

function fmtEur(value: number, locale: string): string {
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(value);
}

/**
 * Compute the ranked list of signals. Maximum 6 returned (3 pros + 3 cons +
 * any risks). Order: risks first (always shown), then strongest pros, then
 * cons. Callers can slice/filter further.
 */
export function computeSignals(input: SignalInput, l: Localiser): Signal[] {
  const out: Signal[] = [];
  const { t, numberLocale } = l;

  const rating = (input.dealRating ?? "unknown") as DealRating;
  const price = input.currentPriceEur ?? null;
  const fair = input.fairPriceEur ?? null;
  const savings = input.dealSavingsEur ?? null;
  const savingsRaw = input.dealSavingsRawEur ?? savings ?? null;

  // ─── PROS ─────────────────────────────────────────────────────────────
  if (rating === "super") {
    out.push({
      id: "super",
      kind: "pro",
      tone: "positive",
      label: t("web.signal.superDeal"),
      detail:
        savings != null && savings > 0
          ? t("web.signal.belowMarket", { amount: fmtEur(savings, numberLocale) })
          : fair && price
            ? t("web.signal.percentBelow", {
                pct: Math.round(((fair - price) / fair) * 100),
              })
            : undefined,
    });
  } else if (rating === "good") {
    out.push({
      id: "good",
      kind: "pro",
      tone: "positive",
      label: t("web.signal.goodDeal"),
      detail:
        savings != null && savings > 0
          ? t("web.signal.belowMarket", { amount: fmtEur(savings, numberLocale) })
          : undefined,
    });
  }

  if (input.recentPriceDropEur && input.recentPriceDropEur > 100) {
    out.push({
      id: "priceDrop",
      kind: "pro",
      tone: "positive",
      label: t("web.signal.priceDropped"),
      detail: fmtEur(input.recentPriceDropEur, numberLocale),
    });
  }

  const days = daysOnMarket(input);
  if (days != null) {
    if (days <= 2) {
      out.push({
        id: "fresh",
        kind: "pro",
        tone: "positive",
        label: t("web.signal.freshListing"),
      });
    } else if (days >= 60) {
      out.push({
        id: "stale",
        kind: "con",
        tone: "warning",
        label: t("web.signal.staleListing"),
        detail: t("web.signal.daysOn", { days }),
      });
    }
  }

  if (input.sellerType === "pro") {
    out.push({
      id: "seller-pro",
      kind: "pro",
      tone: "neutral",
      label: t("web.signal.sellerPro"),
    });
  } else if (input.sellerType === "private") {
    out.push({
      id: "seller-private",
      kind: "pro",
      tone: "neutral",
      label: t("web.signal.sellerPrivate"),
    });
  }

  const kpy = kmPerYear(input);
  if (kpy != null) {
    if (kpy < 8000 && (input.currentKm ?? 0) > 5000) {
      out.push({
        id: "low-km",
        kind: "pro",
        tone: "positive",
        label: t("web.signal.lowKm"),
        detail: t("web.signal.kmPerYear", { km: kpy.toLocaleString(numberLocale) }),
      });
    } else if (kpy > 25000) {
      out.push({
        id: "high-km",
        kind: "con",
        tone: "warning",
        label: t("web.signal.highKm"),
        detail: t("web.signal.kmPerYear", { km: kpy.toLocaleString(numberLocale) }),
      });
    }
  }

  if (input.dupCount != null && input.dupCount > 1) {
    out.push({
      id: "multi-source",
      kind: "pro",
      tone: "neutral",
      label: t("web.signal.multiSource", { count: input.dupCount }),
    });
  }

  // ─── CONS ─────────────────────────────────────────────────────────────
  if (rating === "high" || rating === "over") {
    out.push({
      id: "above-market",
      kind: "con",
      tone: "negative",
      label: t("web.signal.aboveMarket"),
      detail:
        fair && price && price > fair
          ? t("web.signal.percentAbove", {
              pct: Math.round(((price - fair) / fair) * 100),
            })
          : undefined,
    });
  }

  if (input.photoCount != null && input.photoCount === 0) {
    out.push({
      id: "no-photos",
      kind: "con",
      tone: "negative",
      label: t("web.signal.noPhotos"),
    });
  } else if (input.photoCount != null && input.photoCount < 3) {
    out.push({
      id: "few-photos",
      kind: "con",
      tone: "warning",
      label: t("web.signal.fewPhotos", { count: input.photoCount }),
    });
  }

  // ─── RISKS ────────────────────────────────────────────────────────────
  if (input.savingsSuspect && savingsRaw && savingsRaw > 0) {
    out.push({
      id: "savings-suspect",
      kind: "risk",
      tone: "warning",
      label: t("web.signal.savingsSuspect"),
      detail: t("web.signal.savingsSuspectDetail"),
    });
  } else if (input.fairPriceConfidence === "low" && rating !== "unknown") {
    out.push({
      id: "low-confidence",
      kind: "risk",
      tone: "warning",
      label: t("web.signal.lowConfidence"),
      detail: t("web.signal.lowConfidenceDetail"),
    });
  }

  return out;
}

/** Split a signals list into pros / cons / risks. */
export function partitionSignals(signals: Signal[]): {
  pros: Signal[];
  cons: Signal[];
  risks: Signal[];
} {
  return {
    pros: signals.filter((s) => s.kind === "pro"),
    cons: signals.filter((s) => s.kind === "con"),
    risks: signals.filter((s) => s.kind === "risk"),
  };
}
